/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The "Voorwaarden" a visitor can filter on — DERIVED from the reviewed
 * dossier facts a row already carries, never a box a provider ticks.
 *
 * The comparison ranks on net payout, and on prd the top ten differ by
 * € 18 a year (2026-09-23), so money is not what decides. The facts that do —
 * does it renew behind my back, does leaving cost anything, how far ahead
 * must I cancel, how often am I paid — sit in the aanbieder-dossier
 * (src/lib/dossier.js) and the payout vocabulary (src/lib/payout.js): coded
 * values, each with its provenance, filed in the portal or the admin drawer
 * and review-queued. A provider therefore earns a feature by filing the
 * underlying fact, and loses it the moment a correction lands — there is
 * nothing to game and nothing to verify beyond what the review already
 * verifies.
 *
 * A code never says more than its fact (the rule of the terms surfaces, see
 * termsPresentation.ts): `other` and `not_stated` earn nothing, and only an
 * offer that ENDS by itself counts as "no silent renewal". The filter narrows;
 * it never re-orders — the ranking disclosure on the page stays true word for
 * word.
 *
 * 2026-10-07 (plan-voorwaarden-vergelijking §4.5): the codes read the new
 * fields — `short_notice` is now "cancel up to 1 December or later" (the
 * deadline before year end is the real difference; a notice period never was),
 * `no_exit_fee` reads `exitCost`. The URL codes keep their names, so old links
 * and public/llms.txt stay valid; the LABEL moved where the meaning did
 * (FEATURE_LABEL_KEY).
 *
 * The three codes that read a BINDING fact (deadline, renewal, exit cost) need
 * a `read` terms review, exactly as the row and the fact table do: under any
 * other review those surfaces print "Niet gecontroleerd" instead of the fact,
 * and a filter must never claim what no surface shows. `fast_payout` reads the
 * frequency alone, which every surface prints whatever the review.
 *
 * Pure on purpose (the termsPresentation.ts pattern): no React, no i18n. The
 * comparer maps a code to FEATURE_LABEL_KEY[code] for its label.
 */

import type { PublicDossier, PublicTermsReview } from '../types';
import { COMMON_CANCEL_DEADLINE } from './dossier.js';
import { canonicalPayout } from './payout';
import { termsReviewStatus } from './termsTone';

/** Also the URL allowlist for `?terms=` — data identifiers, never translated. */
export const FEATURE_CODES = [
  'no_silent_renewal',
  'no_exit_fee',
  'short_notice',
  'fast_payout',
] as const;
export type FeatureCode = (typeof FEATURE_CODES)[number];

/**
 * The i18n key of each code's label. `short_notice` and `no_exit_fee` changed
 * meaning on 2026-10-07 and therefore read a NEW key (an i18n_admin override
 * freezes the value of a shipped key): "no exit fee" became "leaving early
 * costs nothing" — no fee AND no forfeited payout. The other two kept theirs.
 */
export const FEATURE_LABEL_KEY: Readonly<Record<FeatureCode, string>> = {
  no_silent_renewal: 'table.filter_terms_no_silent_renewal',
  no_exit_fee: 'table.filter_terms_no_exit_cost',
  short_notice: 'table.filter_terms_late_deadline',
  fast_payout: 'table.filter_terms_fast_payout',
};

export const isFeatureCode = (value: string | null | undefined): value is FeatureCode =>
  FEATURE_CODES.includes(value as FeatureCode);

/** The three fields of a row the derivation reads. */
export interface FeatureInput {
  dossier?: PublicDossier | null;
  payoutFrequency?: string | null;
  /** The public review: the binding codes count only when it is `read`. */
  termsReview?: PublicTermsReview | null;
}

/** Canonical payout spellings that count as "per kwartaal of vaker". */
const FAST_PAYOUT = new Set(['Per maand', 'Per kwartaal']);

/**
 * "Opzeggen kan tot 1 december of later": no deadline before year end at all,
 * or one on the usual 1 December or later.
 */
function lateDeadline(dossier: PublicDossier): boolean {
  const entry = dossier.cancelDeadline;
  if (entry?.value === 'none') return true;
  return (
    entry?.value === 'before_date' &&
    typeof entry.amount === 'number' &&
    entry.amount >= COMMON_CANCEL_DEADLINE
  );
}

export function providerFeatures(row: FeatureInput): FeatureCode[] {
  const dossier = row.dossier ?? {};
  const out: FeatureCode[] = [];
  // The binding facts only under a `read` review (see the header).
  if (termsReviewStatus(row.termsReview) === 'read') {
    // "Loopt af" — the term ends by itself. `renews_yearly` and
    // `renews_same_term` are silent renewals; `other` is prose.
    if (dossier.afterTerm?.value === 'ends') out.push('no_silent_renewal');
    if (dossier.exitCost?.value === 'none') out.push('no_exit_fee');
    if (lateDeadline(dossier)) out.push('short_notice');
  }
  // The canonical spelling folds the legacy "Kwartaal" / "Maandelijks";
  // "Periodiek", "Naar keuze", "Per afspraak" and an unknown value never count.
  const payout = canonicalPayout(row.payoutFrequency ?? null);
  if (payout && FAST_PAYOUT.has(payout)) out.push('fast_payout');
  return out;
}

export const hasFeature = (row: FeatureInput, code: FeatureCode): boolean =>
  providerFeatures(row).includes(code);
